import React, { useEffect, useState } from "react";

import { Skeleton, Typography } from "@packages/ds";
import momentjs from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import * as Accordion from "ds/Accordion";
import { Button } from "ds/Button";
import { WizardStepperWrapper } from "ds/WizardStepper";
import { triggerConfetti } from "Libs/confetti";
import { useOrganizationProfile } from "Reducers/organization/hooks/useOrganizationProfile";
import { loadLoggedInMember } from "Reducers/organization/loggedInMember/thunks/loadLoggedInMember";
import { useLoadRecurringOrder } from "Reducers/organization/order/hooks/useLoadRecurringOrder";
import PaymentElements from "src/organization/pages/billing/details/components/PaymentElements/PaymentElements";
import { trackEventPendo } from "Store/analytics/pendoTracker";
import { useAppDispatch } from "Store/hooks";

import AddressForm from "../billing/details/components/AddressForm";

import * as S from "./BillingDetails.styles";
import { Content, ContentScrollable } from "./Content";
import { useOrganizationEstimate } from "./Review/useOrganizationEstimate";
import { useOrganizationAddress } from "./useOrganizationAddress";
import { useOrganizationAllowedPaymentSources } from "./useOrganizationAllowedPaymentSources";
import { useOrganizationPaymentSource } from "./useOrganizationPaymentSource";

import type { Organization } from "@packages/client";

type Props = {
  organization: Organization;
};

export const BillingDetails = ({ organization }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const paymentSource = useOrganizationPaymentSource(organization.name);
  const allowedPaymentSources = useOrganizationAllowedPaymentSources(
    organization.id
  );
  const [openAccordionItems, setOpenAccordionItems] = useState<Array<string>>([
    "billing_details"
  ]);
  const [hasBillingAddress, setHasBillingAddress] = useState(false);
  const { load: loadOrganizationProfile } = useOrganizationProfile(
    organization.name
  );
  const {
    address: organizationAddress,
    isLoading: isLoadingAddress,
    load: loadOrganizationAddresss
  } = useOrganizationAddress(organization.id, {
    onAddressLoads: address => {
      setHasBillingAddress(!!address.name_line);
      if (address.name_line) {
        setOpenAccordionItems(["billing_details", "payment_method"]);
      }
    }
  });
  const loadedAddress = { ...organizationAddress };

  const [isThanksVisible, setIsThanksVisible] = useState(false);
  const [address, setAddress] = useState(organizationAddress);

  const order = useLoadRecurringOrder(organization.id, organization.name);

  const estimate = useOrganizationEstimate(organization);

  const existingPaymentSourceType = paymentSource.paymentSource?.type;

  useEffect(() => {
    setAddress(organizationAddress);
  }, [organizationAddress]);

  const refreshPayment = () => {
    paymentSource.load();

    setIsThanksVisible(true);
    document.getElementById("scrollableContent")?.scrollTo(0, 0);
    triggerConfetti("body", { y: 0 });
    trackEventPendo("subscribed", {
      value: estimate.data?.total
    });
    window.setTimeout(() => {
      navigate(`/${organization.name}`);
    }, 10000);
  };

  const intl = useIntl();

  const onSaveForm = () => {
    loadOrganizationProfile();
    loadOrganizationAddresss();
  };

  const onSubscribe = () => {
    dispatch(loadLoggedInMember(organization.id));
    refreshPayment();
  };

  const [draftAddress, setDraftAddress] = useState({
    ...address,
    vat_number: "",
    company_name: "",
    billing_contact: ""
  });

  return (
    <Content id="scrollableContent">
      <ContentScrollable>
        <S.SplitLayout>
          {/*Intentional empty space to align the grid layout for desktop and mobile*/}
          <S.OverviewLayout></S.OverviewLayout>
          <S.Form>
            <WizardStepperWrapper
              hasLabel
              steps={[
                {
                  number: 1,
                  status: "completed",
                  label: <FormattedMessage id="subscribe.stepper.review" />,
                  stepPath: `/${organization.name}/-/subscribe`
                },
                {
                  number: 2,
                  status: "active",
                  label: (
                    <FormattedMessage id="subscribe.stepper.billingDetails" />
                  )
                }
              ]}
            />
            <Typography.Title tag="h1">
              <FormattedMessage id="subscribe.billingDetails.title" />
            </Typography.Title>
          </S.Form>
        </S.SplitLayout>
        {isThanksVisible && (
          <S.Thanks>
            <Typography.Title tag="h3">
              <FormattedMessage id="subscribe.billingDetails.thanks.title" /> 🎉
            </Typography.Title>
            <Typography.BodyText size="small" weight="semiBold">
              <FormattedMessage
                id="subscribe.billingDetails.thanks.description"
                values={{
                  a: (...chunks) => (
                    <Button
                      variant="link"
                      to={`/${organization.name}`}
                      analyticId="organization_name"
                    >
                      {chunks}
                    </Button>
                  )
                }}
              />
            </Typography.BodyText>
          </S.Thanks>
        )}
        <S.SplitLayout>
          <S.OverviewLayout>
            <Typography.Title tag="h2">
              <FormattedMessage id="subscribe.billingDetails.upcomingInvoice" />
            </Typography.Title>
            <S.Overview>
              <S.OverviewTitle>
                <Typography.BodyText weight="semiBold">
                  {organization.label}
                </Typography.BodyText>

                {order.loading || !order.order ? (
                  <Skeleton synchronize>
                    <Skeleton.Square height="16px" fixedWidth width="50px" />
                  </Skeleton>
                ) : (
                  <Typography.Label>
                    {momentjs(order.order?.billing_period_start).format("MMMM")}
                  </Typography.Label>
                )}
              </S.OverviewTitle>
              <S.OverviewHr />
              {order.loading || !order.order ? (
                <Skeleton synchronize>
                  <Skeleton.Square height="32px" fixedWidth width="90px" />
                </Skeleton>
              ) : (
                <S.OverviewPrice>
                  <Typography.Title tag="h2">
                    {estimate?.data?.total}
                  </Typography.Title>
                </S.OverviewPrice>
              )}
              <Typography.Label>
                <FormattedMessage id="subscribe.billingDetails.estimatedInvoice" />
              </Typography.Label>
            </S.Overview>
          </S.OverviewLayout>
          {isLoadingAddress ? null : (
            <S.Form>
              <Accordion.Root
                type="multiple"
                value={openAccordionItems}
                onValueChange={a => {
                  setOpenAccordionItems(a);
                }}
              >
                <Accordion.Item value="billing_details">
                  <Accordion.SectionHeader>
                    <FormattedMessage id="subscribe.billingDetails.title" />
                  </Accordion.SectionHeader>
                  <Accordion.Content>
                    <AddressForm
                      organizationId={organization.name}
                      addressData={address}
                      loadedAddress={loadedAddress}
                      onAddressChange={address => {
                        setAddress(address);
                        setDraftAddress(address);
                        allowedPaymentSources.load();
                      }}
                      draftAddress={draftAddress}
                      onDraftAddressChange={setDraftAddress}
                      finalize={() => {
                        onSaveForm();
                        setOpenAccordionItems(["payment_method"]);
                        setHasBillingAddress(true);
                      }}
                    />
                  </Accordion.Content>
                </Accordion.Item>

                {existingPaymentSourceType !== "invoice_account" && (
                  <S.PaymentMethods>
                    <Accordion.Item
                      value="payment_method"
                      disabled={!hasBillingAddress}
                    >
                      <Accordion.SectionHeader disabled={!hasBillingAddress}>
                        <FormattedMessage id="payment_method" />
                      </Accordion.SectionHeader>
                      <Accordion.Content>
                        <PaymentElements
                          organizationId={organization.id}
                          organizationDescriptionId={organization.name}
                          onSave={onSubscribe}
                          customSubmitLabel={intl.formatMessage({
                            id: "subscribe.billingDetails.subscribe"
                          })}
                        />
                      </Accordion.Content>
                      {!hasBillingAddress &&
                        intl.formatMessage({
                          id: "billing.details.payment_method.note"
                        })}
                    </Accordion.Item>
                  </S.PaymentMethods>
                )}
              </Accordion.Root>
            </S.Form>
          )}
        </S.SplitLayout>
      </ContentScrollable>
    </Content>
  );
};
